import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Box, useMediaQuery } from "@mui/material";

// Register necessary Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface HistoricalChartProps {
  data: { [key: string]: number };
}

const HistoricalChart: React.FC<HistoricalChartProps> = ({ data }) => {
  const isMobile = useMediaQuery("(max-width:1200px)");

  // Create gradient
  const createGradient = (ctx: CanvasRenderingContext2D) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, "rgba(99, 102, 241, 0.8)");
    gradient.addColorStop(0.5, "rgba(139, 92, 246, 0.4)");
    gradient.addColorStop(1, "rgba(167, 139, 250, 0.1)");
    return gradient;
  };

  // Prepare chart data
  const chartData = {
    labels: Object.keys(data), // Use dates as labels
    datasets: [
      {
        label: "IT Jobs in Netherlands",
        data: Object.values(data), // Use the values as data
        borderColor: "#6366f1",
        backgroundColor: function (context: any) {
          const ctx = context.chart.ctx;
          return createGradient(ctx);
        },
        fill: true,
        pointRadius: 6,
        pointHoverRadius: 10,
        pointBackgroundColor: "#6366f1",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#6366f1",
        pointHoverBorderWidth: 3,
        tension: 0.4,
        borderWidth: 3,
      },
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          font: {
            size: 13,
            weight: "bold" as const,
            family: "'Inter', sans-serif",
          },
          color: "#273848",
          padding: 15,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        titleColor: "#273848",
        bodyColor: "#273848",
        borderColor: "#e0e0e0",
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        titleFont: {
          size: 14,
          weight: "bold" as const,
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: (context: any) => {
            return ` Jobs: ${context.parsed.y.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
            family: "'Inter', sans-serif",
          },
          color: "#64748b",
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: {
          color: "rgba(148, 163, 184, 0.15)",
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
            family: "'Inter', sans-serif",
          },
          color: "#64748b",
          padding: 8,
        },
        border: {
          display: false,
        },
      },
    },
    interaction: {
      mode: "nearest" as const,
      axis: "x" as const,
      intersect: false,
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: isMobile ? 250 : 320,
        padding: isMobile ? 2 : 3,
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      }}
    >
      <Line data={chartData} options={options} />
    </Box>
  );
};

export default HistoricalChart;
